import { useEffect, useState } from 'react';
import {
  aboutContent as defaultAboutContent,
  faqData as defaultFaqData,
  resources as defaultResources,
  specialties as defaultSpecialties,
  testimonials as defaultTestimonials,
} from '../mockData';

const DEFAULT_CORE_VALUES = [
  {
    title: 'Compassion',
    description: 'Providing empathetic, understanding support throughout your wellness journey',
  },
  {
    title: 'Professionalism',
    description: 'Maintaining the highest standards of care and ethical practice',
  },
  {
    title: 'Accessibility',
    description: 'Making wellness services available both locally and worldwide online',
  },
];

const DEFAULT_SECTIONS_CONTENT = {
  about: {
    title: defaultAboutContent.title,
    subtitle: defaultAboutContent.subtitle,
    image: defaultAboutContent.image,
    bio: defaultAboutContent.bio,
    approachParagraph:
      "I believe in a holistic approach that addresses both mental and physical wellbeing. Through personalized coaching sessions and therapeutic massage, I help clients develop sustainable strategies for managing their mental health while nurturing their body's natural healing abilities.",
    credentials: defaultAboutContent.credentials,
    languages: defaultAboutContent.languages,
    serviceAreaLine1: 'Munich (In-person)',
    serviceAreaLine2: 'Worldwide (Online)',
    commitmentText:
      'I am committed to ongoing professional development and staying current with the latest research and techniques in mental health coaching and therapeutic massage. Your wellbeing is my priority.',
    philosophyTitle: 'My Philosophy',
    philosophyParagraph1:
      "Every individual's journey is unique. I provide a safe, non-judgmental space where you can explore your challenges, develop coping strategies, and work towards your goals at your own pace.",
    philosophyParagraph2:
      "Whether you're dealing with depression, PTSD, ADHD, or simply seeking better balance in your life, I'm here to support you every step of the way.",
  },
  specialties: defaultSpecialties,
  testimonials: defaultTestimonials,
  coreValues: DEFAULT_CORE_VALUES,
  faqs: defaultFaqData,
  resources: defaultResources,
};

const fetchSectionsContent = async () => {
  const response = await fetch('/api/content/sections', {
    method: 'GET',
    headers: {
      Accept: 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to load sections content: ${response.status}`);
  }

  return response.json();
};

export const useSectionsContent = () => {
  const [sectionsContent, setSectionsContent] = useState(DEFAULT_SECTIONS_CONTENT);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let isCancelled = false;

    const load = async () => {
      try {
        const payload = await fetchSectionsContent();
        if (isCancelled || !payload || typeof payload !== 'object') {
          return;
        }

        setSectionsContent((current) => ({
          ...current,
          ...payload,
          about: {
            ...current.about,
            ...(payload.about || {}),
          },
        }));
      } catch (error) {
        if (!isCancelled) {
          console.error('Failed to load sections content from backend:', error);
        }
      } finally {
        if (!isCancelled) {
          setIsLoading(false);
        }
      }
    };

    load();

    return () => {
      isCancelled = true;
    };
  }, []);

  return {
    sectionsContent,
    isLoading,
  };
};
